import { appElement } from '@opengov/compose-app/renderApp';
import { ThemeProvider } from '@mui/material';
import { storiesAppTheme } from './theme';
import '@opengov/capital-mui-theme/dist/fonts.css';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ShellAuth0Provider } from '@opengov/shell-auth0-react';
import type { ModuleMountOptions } from '@opengov/psp-contract';

import { rootView as RootView } from './rootView';
import { ToastProvider } from './stories/components/Toast/context/Toast.provider';
function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache(),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false
      }
    }
  });
}

// `queryClient` is `let` so PSP unmount can swap in a fresh client between
// mount cycles. Consumers read via `useQueryClient()` and pick up the new
// client through QueryClientProvider on the next render.
let queryClient = createQueryClient();

export function resetQueryClient(): void {
  queryClient = createQueryClient();
}

let root: Root | null = null;


export const renderer = (store) => {
  store.subscribe(() => {
    if (root === null) {
      if (!appElement) {
        throw new Error('compose-app appElement is missing — DOM mount point not found');
      }
      root = createRoot(appElement);
    }
    // Every descendant that calls useShellAuth0() (bnp-component-navbar's
    // NavBarView → useUnreadCount → useFetchQuery → useSafeAuth0GetAccessToken,
    // bnp-core's UsersAndGroupsContextProvider, bnp-component-workflows-widgets)
    // needs a ShellAuth0Provider ancestor in BOTH build modes.
    //
    // PSP mode: wrap in <ShellAuth0Provider mode="shell" shellBridge={...}>
    //   reading the bridge handed to us by the PSP shell at mount time.
    // Standalone mode: wrap in <ShellAuth0Provider mode="standalone" {...auth0Config}>
    //   using the same domain/clientId/redirect/audience the rest of the app
    //   gets from window.process.env (injected by index.html via Vite's
    //   htmlInjectionPlugin from the root .env file).
    const pspOptions = (window as unknown as {
      __OG_STORIES_PSP_OPTIONS__?: ModuleMountOptions;
    }).__OG_STORIES_PSP_OPTIONS__;

    const env = (window as unknown as { process?: { env?: Record<string, string | undefined> } })
      .process?.env;
    const standaloneAuth0Props = {
      domain: env?.AUTH0_DOMAIN ?? '',
      clientId: env?.AUTH0_CLIENT_ID ?? '',
      authorizationParams: {
        redirect_uri: typeof window !== 'undefined' ? window.location.origin : undefined,
        audience: 'opengov'
      },
      cacheLocation: 'localstorage' as const,
      useRefreshTokens: true
    };

    const tree = (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={storiesAppTheme}>
          <ToastProvider>
            <RootView state={store.getState()} />
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    );

    root.render(
      pspOptions ? (
        // tslint:disable-next-line:no-non-null-assertion
        <ShellAuth0Provider mode="shell" shellBridge={pspOptions.shell!}>
          {tree}
        </ShellAuth0Provider>
      ) : (
        <ShellAuth0Provider mode="standalone" {...standaloneAuth0Props}>
          {tree}
        </ShellAuth0Provider>
      )
    );
  });
};

// Tear down the React tree on PSP unmount. Releases React 18 reconciler state
// bound to `appElement`. The DOM element itself stays alive in memory (the
// singleton DOM approach in pspEntry.tsx keeps `#app` referenced) so
// compose-app's module-level `appElement` capture remains valid for the next
// mount cycle. On next mount, the store-subscribe path above recreates `root`
// lazily against the same `appElement`.
export function tearDownReactRoot(): void {
  if (root !== null) {
    root.unmount();
    root = null;
  }
}
